import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GraduationCap, Loader2, CheckCircle2, Upload, X, FileText, AlertCircle, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Common country codes for international phone
const COUNTRY_CODES = [
  { code: '+1', country: 'US/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
  { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
  { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪' },
];

const PublicFormPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState([]); // { fieldId, docTypeId, docTypeName, id, filename, file_size }
  const [uploading, setUploading] = useState({});
  const fileInputRefs = useRef({});
  
  // Master data
  const [countries, setCountries] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [degreeTypes, setDegreeTypes] = useState([]);
  const [universities, setUniversities] = useState([]);
  
  // Conditional documents state
  const [conditionalDocs, setConditionalDocs] = useState([]); // Dynamic required docs based on university+country+degree
  const [loadingConditionalDocs, setLoadingConditionalDocs] = useState(false);
  const [conditionalDocsMessage, setConditionalDocsMessage] = useState('');
  
  // Search states for dropdowns
  const [programSearch, setProgramSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [nationalitySearch, setNationalitySearch] = useState('');
  const [degreeSearch, setDegreeSearch] = useState('');
  const [universitySearch, setUniversitySearch] = useState('');

  useEffect(() => {
    fetchForm();
  }, [slug]);

  const fetchForm = async () => {
    try {
      const response = await axios.get(`${API}/public/forms/${slug}`);
      setForm(response.data);
      
      // Fetch master data for special fields
      const hasUniversityField = response.data.fields?.some(f => f.type === 'university_select');
      const hasCountryField = response.data.fields?.some(f => f.type === 'country_select');
      const hasNationalityField = response.data.fields?.some(f => f.type === 'nationality_select');
      const hasProgramField = response.data.fields?.some(f => f.type === 'program_select');
      const hasDocumentField = response.data.fields?.some(f => f.type === 'document_upload' || f.type === 'documents_list');
      const hasDegreeTypeField = response.data.fields?.some(f => f.type === 'degree_type_select');
      const hasConditionalDocs = response.data.fields?.some(f => f.type === 'conditional_documents');
      
      // Need countries for both country_select and nationality_select fields
      const needsCountries = hasCountryField || hasNationalityField || hasConditionalDocs;
      
      const promises = [];
      if (hasUniversityField || hasConditionalDocs) promises.push(axios.get(`${API}/public/forms/${slug}/master-data?type=university`));
      if (needsCountries) promises.push(axios.get(`${API}/public/forms/${slug}/master-data?type=country`));
      if (hasProgramField) promises.push(axios.get(`${API}/public/forms/${slug}/master-data?type=program`));
      if (hasDocumentField) promises.push(axios.get(`${API}/public/forms/${slug}/master-data?type=document_type`));
      if (hasDegreeTypeField || hasConditionalDocs) promises.push(axios.get(`${API}/public/forms/${slug}/master-data?type=degree_type`));
      
      if (promises.length > 0) {
        const results = await Promise.all(promises);
        let idx = 0;
        if (hasUniversityField || hasConditionalDocs) setUniversities(results[idx++]?.data || []);
        if (needsCountries) setCountries(results[idx++]?.data || []);
        if (hasProgramField) setPrograms(results[idx++]?.data || []);
        if (hasDocumentField) setDocumentTypes(results[idx++]?.data || []);
        if (hasDegreeTypeField || hasConditionalDocs) setDegreeTypes(results[idx++]?.data || []);
      }
      
      // Initialize form data with empty values
      const initialData = {};
      if (response.data.fields) {
        for (let i = 0; i < response.data.fields.length; i++) {
          const field = response.data.fields[i];
          if (field.type === 'checkbox') {
            initialData[field.id] = false;
          } else if (field.type === 'phone_intl') {
            initialData[field.id] = { countryCode: '+1', number: '' };
          } else if (field.type === 'document_upload') {
            initialData[field.id] = { docTypeId: '', docTypeName: '' };
          } else {
            initialData[field.id] = '';
          }
        }
      }
      setFormData(initialData);
    } catch (err) {
      setError('Form not found or not available');
    } finally {
      setLoading(false);
    }
  };

  // Fetch conditional required documents when university, country or degree changes
  const fetchConditionalDocuments = async (university, country, degreeType) => {
    // Need at least country and degree, university is optional (can come from form's linked university)
    if (!country || !degreeType) {
      setConditionalDocs([]);
      setConditionalDocsMessage('');
      return;
    }
    
    setLoadingConditionalDocs(true);
    try {
      const params = { country, degree_type: degreeType };
      if (university) params.university = university;
      
      const response = await axios.get(
        `${API}/public/forms/${slug}/required-documents`,
        { params }
      );
      setConditionalDocs(response.data.required_documents || []);
      setConditionalDocsMessage(response.data.message || '');
    } catch (err) {
      console.error('Failed to fetch required documents:', err);
      setConditionalDocs([]);
      setConditionalDocsMessage('Failed to load document requirements');
    } finally {
      setLoadingConditionalDocs(false);
    }
  };

  // Get the selected values from form data
  const getSelectedUniversity = () => {
    if (!form?.fields) return null;
    const uniField = form.fields.find(f => f.type === 'university_select');
    return uniField ? formData[uniField.id] : null;
  };

  // Get selected nationality (for conditional documents - checks both nationality_select and country_select)
  const getSelectedNationality = () => {
    if (!form?.fields) return null;
    // First try nationality_select, then fall back to country_select
    const nationalityField = form.fields.find(f => f.type === 'nationality_select');
    if (nationalityField) return formData[nationalityField.id];
    const countryField = form.fields.find(f => f.type === 'country_select');
    return countryField ? formData[countryField.id] : null;
  };

  const getSelectedDegreeType = () => {
    if (!form?.fields) return null;
    const degreeField = form.fields.find(f => f.type === 'degree_type_select');
    return degreeField ? formData[degreeField.id] : null;
  };

  // Effect to fetch conditional docs when nationality or degree changes
  // University is automatically taken from form's linked university (no manual selection needed)
  useEffect(() => {
    const selectedNationality = getSelectedNationality();
    const selectedDegree = getSelectedDegreeType();
    
    // Check if form has conditional_documents field
    const hasConditionalDocs = form?.fields?.some(f => f.type === 'conditional_documents');
    
    if (hasConditionalDocs && selectedNationality && selectedDegree) {
      // Pass null for university - backend will use form's linked university
      fetchConditionalDocuments(null, selectedNationality, selectedDegree);
    }
  }, [formData, form]);

  const handleInputChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleFileUpload = async (e, fieldId, docTypeId, docTypeName) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(prev => ({ ...prev, [fieldId]: true }));
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        if (docTypeId) {
          formDataUpload.append('document_type_id', docTypeId);
          formDataUpload.append('document_type_name', docTypeName);
        }
        
        const response = await axios.post(
          `${API}/public/forms/${slug}/upload`,
          formDataUpload,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        setUploadedDocs(prev => [...prev, {
          fieldId,
          docTypeId: docTypeId || null,
          docTypeName: docTypeName || null,
          id: response.data.id,
          filename: file.name,
          file_type: response.data.file_type,
          file_size: response.data.file_size,
          file_id: response.data.file_id,  // GridFS reference
          storage: response.data.storage || 'gridfs'
        }]);
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }

    setUploading(prev => ({ ...prev, [fieldId]: false }));
    if (fileInputRefs.current[fieldId]) {
      fileInputRefs.current[fieldId].value = '';
    }
  };

  const removeDocument = (docId) => {
    setUploadedDocs(prev => prev.filter(d => d.id !== docId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (form?.fields) {
      for (let i = 0; i < form.fields.length; i++) {
        const field = form.fields[i];
        if (field.required) {
          const value = formData[field.id];
          if (field.type === 'phone_intl') {
            if (!value?.number) {
              alert(`Please fill in: ${field.label}`);
              return;
            }
          } else if (field.type === 'document_upload') {
            // Check if any document uploaded for this field
            const fieldDocs = uploadedDocs.filter(d => d.fieldId === field.id);
            if (fieldDocs.length === 0) {
              alert(`Please upload: ${field.label}`);
              return;
            }
          } else if (field.type === 'conditional_documents') {
            // Check if all conditional documents are uploaded (only if there are requirements)
            if (conditionalDocs.length > 0) {
              const fieldDocs = uploadedDocs.filter(d => d.fieldId === field.id);
              if (fieldDocs.length < conditionalDocs.length) {
                const missing = conditionalDocs.filter(docName => 
                  !fieldDocs.some(d => d.docTypeName?.toLowerCase() === docName.toLowerCase())
                );
                alert(`Please upload all required documents. Missing: ${missing.join(', ')}`);
                return;
              }
            }
          } else if (!value) {
            alert(`Please fill in: ${field.label}`);
            return;
          }
        }
      }
    }

    setSubmitting(true);
    try {
      // Build submission data mapping field IDs to application fields
      const submissionData = {};
      if (form?.fields) {
        for (let i = 0; i < form.fields.length; i++) {
          const field = form.fields[i];
          const fieldKey = field.applicationField || field.label.toLowerCase().replace(/\s+/g, '_');
          const value = formData[field.id];
          
          if (field.type === 'phone_intl') {
            // Combine country code and number
            submissionData[fieldKey] = value?.number ? `${value.countryCode} ${value.number}` : '';
          } else if (field.type === 'document_upload' || field.type === 'conditional_documents') {
            // Skip - handled separately via documents array
          } else {
            submissionData[fieldKey] = value;
          }
        }
      }

      // Prepare documents with type info and GridFS reference
      const documentsPayload = uploadedDocs.map(d => ({
        id: d.id,
        document_type_id: d.docTypeId,
        document_type_name: d.docTypeName,
        filename: d.filename,
        file_type: d.file_type,
        file_id: d.file_id,  // GridFS reference
        storage: d.storage || 'gridfs'
      }));

      await axios.post(`${API}/public/forms/${slug}/submit`, {
        data: submissionData,
        documents: documentsPayload,
        source: isEmbed ? 'Web Form (Embed)' : 'Web Form'
      });
      
      setSubmitted(true);
    } catch (err) {
      alert('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderField = (field) => {
    const value = formData[field.id];
    
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <Input
            type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
            value={value || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
          />
        );
      
      case 'phone_intl':
        return (
          <div className="flex gap-2">
            <Select 
              value={value?.countryCode || '+1'} 
              onValueChange={(v) => handleInputChange(field.id, { ...value, countryCode: v })}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_CODES.map(cc => (
                  <SelectItem key={cc.code} value={cc.code}>
                    {cc.flag} {cc.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="tel"
              value={value?.number || ''}
              onChange={(e) => handleInputChange(field.id, { ...value, number: e.target.value })}
              placeholder="Phone number"
              className="flex-1"
              required={field.required}
            />
          </div>
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            required={field.required}
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={4}
          />
        );
      
      case 'select':
        const selectOptions = [];
        if (field.options) {
          for (let i = 0; i < field.options.length; i++) {
            selectOptions.push(
              <SelectItem key={i} value={field.options[i]}>{field.options[i]}</SelectItem>
            );
          }
        }
        return (
          <Select value={value || ''} onValueChange={(v) => handleInputChange(field.id, v)}>
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || 'Select an option'} />
            </SelectTrigger>
            <SelectContent>{selectOptions}</SelectContent>
          </Select>
        );
      
      case 'university_select':
        const filteredUniversities = universities.filter(u => 
          u.name.toLowerCase().includes(universitySearch.toLowerCase())
        );
        return (
          <Select value={value || ''} onValueChange={(v) => { handleInputChange(field.id, v); setUniversitySearch(''); }}>
            <SelectTrigger>
              <SelectValue placeholder="Select university" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <div className="sticky top-0 p-2 bg-background border-b">
                <Input
                  placeholder="Search universities..."
                  value={universitySearch}
                  onChange={(e) => setUniversitySearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-8"
                />
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filteredUniversities.length > 0 ? (
                  filteredUniversities.map(u => (
                    <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                    {universities.length === 0 ? 'No universities available' : 'No matching universities'}
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        );
      
      case 'country_select':
        const filteredCountries = countries.filter(c => 
          c.name.toLowerCase().includes(countrySearch.toLowerCase())
        );
        return (
          <Select value={value || ''} onValueChange={(v) => { handleInputChange(field.id, v); setCountrySearch(''); }}>
            <SelectTrigger>
              <SelectValue placeholder="Select your country" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <div className="sticky top-0 p-2 bg-background border-b">
                <Input
                  placeholder="Search countries..."
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-8"
                />
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map(c => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                    {countries.length === 0 ? 'No countries available' : 'No matching countries'}
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        );
      
      case 'nationality_select':
        const filteredNationalities = countries.filter(c => 
          c.name.toLowerCase().includes(nationalitySearch.toLowerCase())
        );
        return (
          <Select value={value || ''} onValueChange={(v) => { handleInputChange(field.id, v); setNationalitySearch(''); }}>
            <SelectTrigger>
              <SelectValue placeholder="Select your nationality" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <div className="sticky top-0 p-2 bg-background border-b">
                <Input
                  placeholder="Search nationalities..."
                  value={nationalitySearch}
                  onChange={(e) => setNationalitySearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-8"
                />
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filteredNationalities.length > 0 ? (
                  filteredNationalities.map(c => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                    {countries.length === 0 ? 'No nationalities available' : 'No matching nationalities'}
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        );
      
      case 'program_select':
        const filteredPrograms = programs.filter(p => 
          p.name.toLowerCase().includes(programSearch.toLowerCase())
        );
        return (
          <Select value={value || ''} onValueChange={(v) => { handleInputChange(field.id, v); setProgramSearch(''); }}>
            <SelectTrigger>
              <SelectValue placeholder="Select program" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <div className="sticky top-0 p-2 bg-background border-b">
                <Input
                  placeholder="Search programs..."
                  value={programSearch}
                  onChange={(e) => setProgramSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-8"
                />
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filteredPrograms.length > 0 ? (
                  filteredPrograms.map(p => (
                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                    {programs.length === 0 ? 'No programs available' : 'No matching programs'}
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        );
      
      case 'degree_type_select':
        // Use fetched degree types from Core Settings, fallback to hardcoded if empty
        const degreeTypeOptions = degreeTypes.length > 0 
          ? degreeTypes.map(dt => dt.name) 
          : (field.options?.length > 0 ? field.options : ['Bachelor', 'Master', 'PhD', 'Diploma', 'Certificate', 'Associate']);
        const filteredDegreeTypes = degreeTypeOptions.filter(dt => 
          dt.toLowerCase().includes(degreeSearch.toLowerCase())
        );
        return (
          <Select value={value || ''} onValueChange={(v) => { handleInputChange(field.id, v); setDegreeSearch(''); }}>
            <SelectTrigger>
              <SelectValue placeholder="Select degree type" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <div className="sticky top-0 p-2 bg-background border-b">
                <Input
                  placeholder="Search degree types..."
                  value={degreeSearch}
                  onChange={(e) => setDegreeSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-8"
                />
              </div>
              <div className="max-h-52 overflow-y-auto">
                {filteredDegreeTypes.length > 0 ? (
                  filteredDegreeTypes.map((dt, idx) => (
                    <SelectItem key={idx} value={dt}>{dt}</SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                    No matching degree types
                  </div>
                )}
              </div>
            </SelectContent>
          </Select>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.id}
              checked={value || false}
              onCheckedChange={(checked) => handleInputChange(field.id, checked)}
            />
            <label htmlFor={field.id} className="text-sm">
              {field.placeholder || 'Yes'}
            </label>
          </div>
        );
      
      case 'file':
        const fieldDocs = uploadedDocs.filter(d => d.fieldId === field.id);
        return (
          <div className="space-y-2">
            <input
              type="file"
              ref={el => fileInputRefs.current[field.id] = el}
              onChange={(e) => handleFileUpload(e, field.id, null, null)}
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRefs.current[field.id]?.click()}
              disabled={uploading[field.id]}
              className="w-full"
            >
              {uploading[field.id] ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Files
            </Button>
            {fieldDocs.length > 0 && (
              <div className="space-y-2">
                {fieldDocs.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      <span className="text-sm truncate max-w-[200px]">{doc.filename}</span>
                      <span className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => removeDocument(doc.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      
      case 'document_upload':
        const docTypeValue = value?.docTypeId || '';
        const docTypeName = value?.docTypeName || '';
        const typedDocs = uploadedDocs.filter(d => d.fieldId === field.id);
        
        return (
          <div className="space-y-3">
            {/* Document Type Selector */}
            <Select 
              value={docTypeValue} 
              onValueChange={(v) => {
                const selectedType = documentTypes.find(dt => dt.id === v);
                handleInputChange(field.id, { 
                  docTypeId: v, 
                  docTypeName: selectedType?.name || '' 
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                {documentTypes.map(dt => (
                  <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Upload Button - only show if type selected */}
            {docTypeValue && (
              <>
                <input
                  type="file"
                  ref={el => fileInputRefs.current[field.id] = el}
                  onChange={(e) => handleFileUpload(e, field.id, docTypeValue, docTypeName)}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRefs.current[field.id]?.click()}
                  disabled={uploading[field.id]}
                  className="w-full"
                >
                  {uploading[field.id] ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload {docTypeName || 'Document'}
                </Button>
              </>
            )}
            
            {/* Uploaded documents list */}
            {typedDocs.length > 0 && (
              <div className="space-y-2">
                {typedDocs.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FileText className="w-4 h-4 text-green-600 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm font-medium block truncate">{doc.docTypeName}</span>
                        <span className="text-xs text-muted-foreground block truncate">{doc.filename}</span>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => removeDocument(doc.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            <p className="text-xs text-muted-foreground">
              Select a document type first, then upload the corresponding file. You can add multiple documents.
            </p>
          </div>
        );
      
      case 'documents_list':
        // Documents List - shows all document types with upload buttons for each
        const listDocs = uploadedDocs.filter(d => d.fieldId === field.id);
        return (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Upload required documents:</p>
            
            {/* Show all document types as a checklist with upload */}
            <div className="space-y-3">
              {documentTypes.map(dt => {
                const docsForType = listDocs.filter(d => d.docTypeId === dt.id);
                const inputKey = `${field.id}_${dt.id}`;
                
                return (
                  <div key={dt.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {docsForType.length > 0 ? (
                          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                        )}
                        <span className="font-medium text-sm">{dt.name}</span>
                      </div>
                      
                      <div>
                        <input
                          type="file"
                          ref={el => fileInputRefs.current[inputKey] = el}
                          onChange={(e) => handleFileUpload(e, field.id, dt.id, dt.name)}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant={docsForType.length > 0 ? "outline" : "default"}
                          size="sm"
                          onClick={() => fileInputRefs.current[inputKey]?.click()}
                          disabled={uploading[inputKey]}
                        >
                          {uploading[inputKey] ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <Upload className="w-3 h-3 mr-1" />
                          )}
                          {docsForType.length > 0 ? 'Replace' : 'Upload'}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Show uploaded file for this type */}
                    {docsForType.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between pl-7 text-sm">
                        <div className="flex items-center gap-2 text-green-700">
                          <FileText className="w-4 h-4" />
                          <span className="truncate max-w-[200px]">{doc.filename}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={() => removeDocument(doc.id)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            
            {listDocs.length > 0 && (
              <p className="text-xs text-green-600">
                {listDocs.length} document(s) uploaded
              </p>
            )}
          </div>
        );
      
      case 'conditional_documents':
        // Smart Documents - dynamically shows document requirements based on form's university + selected nationality + degree type
        const selectedNationality = getSelectedNationality();
        const selectedDegree = getSelectedDegreeType();
        const conditionalFieldDocs = uploadedDocs.filter(d => d.fieldId === field.id);
        
        // Build missing fields message - only Nationality and Degree Type required (University comes from form)
        const missingFields = [];
        if (!selectedNationality) missingFields.push('Nationality');
        if (!selectedDegree) missingFields.push('Degree Type');
        
        if (missingFields.length > 0) {
          return (
            <div className="p-4 bg-muted/50 border border-dashed rounded-lg text-center">
              <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Please select {missingFields.map((f, i) => (
                  <span key={f}><strong>{f}</strong>{i < missingFields.length - 1 ? ' and ' : ''}</span>
                ))} above to see required documents.
              </p>
            </div>
          );
        }
        
        if (loadingConditionalDocs) {
          return (
            <div className="p-4 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span className="text-sm text-muted-foreground">Loading required documents...</span>
            </div>
          );
        }
        
        if (conditionalDocs.length === 0) {
          return (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
              <AlertCircle className="w-6 h-6 mx-auto text-amber-500 mb-2" />
              <p className="text-sm text-amber-700">
                {conditionalDocsMessage || `No specific document requirements found for ${selectedNationality} - ${selectedDegree}.`}
              </p>
              <p className="text-xs text-amber-600 mt-1">
                You may proceed without uploading documents, or contact the institution for guidance.
              </p>
            </div>
          );
        }
        
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Required documents for <strong>{selectedNationality}</strong> - <strong>{selectedDegree}</strong>:
              </p>
              <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                {conditionalDocs.length} required
              </span>
            </div>
            
            {/* Show each required document with upload */}
            <div className="space-y-3">
              {conditionalDocs.map((docName, idx) => {
                const docsForType = conditionalFieldDocs.filter(d => 
                  d.docTypeName?.toLowerCase() === docName.toLowerCase()
                );
                const inputKey = `${field.id}_conditional_${idx}`;
                
                return (
                  <div key={idx} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {docsForType.length > 0 ? (
                          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                            <CheckCircle2 className="w-3 h-3 text-white" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                        )}
                        <span className="font-medium text-sm">{docName}</span>
                      </div>
                      
                      <div>
                        <input
                          type="file"
                          ref={el => fileInputRefs.current[inputKey] = el}
                          onChange={(e) => handleFileUpload(e, field.id, `conditional_${idx}`, docName)}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant={docsForType.length > 0 ? "outline" : "default"}
                          size="sm"
                          onClick={() => fileInputRefs.current[inputKey]?.click()}
                          disabled={uploading[inputKey]}
                          data-testid={`upload-btn-${docName.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {uploading[inputKey] ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <Upload className="w-3 h-3 mr-1" />
                          )}
                          {docsForType.length > 0 ? 'Replace' : 'Upload'}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Show uploaded file for this type */}
                    {docsForType.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between pl-7 text-sm">
                        <div className="flex items-center gap-2 text-green-700">
                          <FileText className="w-4 h-4" />
                          <span className="truncate max-w-[200px]">{doc.filename}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={() => removeDocument(doc.id)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            
            {conditionalFieldDocs.length > 0 && (
              <p className="text-xs text-green-600">
                {conditionalFieldDocs.length} of {conditionalDocs.length} document(s) uploaded
              </p>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  const renderFormFields = () => {
    if (!form?.fields) return null;
    const items = [];
    for (let i = 0; i < form.fields.length; i++) {
      const field = form.fields[i];
      items.push(
        <div key={field.id} className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {renderField(field)}
        </div>
      );
    }
    return items;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-bold mb-2">Form Not Found</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
            <p className="text-muted-foreground">
              Your application has been submitted successfully. We will review your application and get back to you soon.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-5 mb-2">
            {form?.university_logo_url ? (
              <img
                src={form.university_logo_url.startsWith('http')
                  ? form.university_logo_url
                  : `${process.env.REACT_APP_BACKEND_URL}${form.university_logo_url}`}
                alt={form.university_name || form.company_name || 'Logo'}
                className="w-24 h-24 sm:w-28 sm:h-28 object-contain rounded-xl bg-white border border-border p-2 shadow-sm"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                data-testid="public-form-university-logo"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-primary rounded-xl flex items-center justify-center">
                <GraduationCap className="w-12 h-12 text-primary-foreground" />
              </div>
            )}
            <span
              className="font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-foreground text-left max-w-[420px] leading-tight"
              data-testid="public-form-brand-name"
            >
              {form?.university_name || form?.company_name}
            </span>
          </div>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>{form?.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {renderFormFields()}
              
              <Button type="submit" className="w-full" disabled={submitting} size="lg">
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Submit Application
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Powered by EduBridge Network
        </p>
      </div>
    </div>
  );
};

export default PublicFormPage;
